import { Link, useNavigate } from 'react-router-dom'
import { Search, Play, FileText, BookOpen, BarChart2, Image, Video, Database, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useVideos } from '@/hooks/useVideos'
import { useContacts } from '@/hooks/useContacts'
import { useActivityFeed } from '@/hooks/useActivityFeed'
import { useKnowledgeSummary } from '@/hooks/useKnowledgeSummary'
import { timeAgo } from '@/lib/utils'
import { CONTENT_TYPE_META } from '@/lib/config'
import type { ContentType } from '@/types'

const SUMMARY_ORDER: ContentType[] = ['bank_data', 'dokumen', 'publikasi', 'laporan', 'infografik', 'media']

const SUMMARY_ICONS: Record<ContentType, React.ReactNode> = {
  bank_data:  <Database size={22} className="text-blue-600" />,
  dokumen:    <FileText size={22} className="text-cerah-teal" />,
  publikasi:  <BookOpen size={22} className="text-green-600" />,
  laporan:    <BarChart2 size={22} className="text-orange-500" />,
  infografik: <Image size={22} className="text-cerah-teal" />,
  media:      <Video size={22} className="text-blue-500" />,
}

const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
  bank_data:  <Database size={14} className="text-blue-600" />,
  dokumen:    <FileText size={14} className="text-cerah-teal" />,
  publikasi:  <BookOpen size={14} className="text-green-600" />,
  laporan:    <BarChart2 size={14} className="text-orange-500" />,
  infografik: <Image size={14} className="text-purple-500" />,
  media:      <Video size={14} className="text-blue-500" />,
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const { videos, loading: videosLoading } = useVideos(5)
  const { contacts, loading: contactsLoading } = useContacts(5)
  const { items: activity, loading: activityLoading } = useActivityFeed(6)
  const { getCount, loading: summaryLoading } = useKnowledgeSummary()

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    if (searchQuery.trim()) navigate(`/app/search?q=${encodeURIComponent(searchQuery.trim())}`)
  }

  return (
    <div className="flex h-full">
      {/* ── Main content ── */}
      <div className="flex-1 min-w-0 overflow-y-auto p-6 flex flex-col gap-6">

        {/* Hero banner */}
        <div className="rounded-2xl bg-cerah-navy overflow-hidden flex items-center justify-between pl-8 relative min-h-[180px]">
          <div className="z-10 flex-1 py-8">
            <p className="text-white/80 text-base mb-1">Selamat Datang di</p>
            <h1 className="text-white text-3xl font-bold mb-2">CERAH Pedia</h1>
            <p className="text-white/60 text-sm max-w-xs mb-5">
              Pusat pengetahuan dan informasi untuk mendukung kolaborasi, transparansi, dan inovasi di lingkungan CERAH.
            </p>
            <form onSubmit={handleSearch} className="flex gap-2 max-w-sm">
              <div className="flex-1 relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  placeholder="Cari pengetahuan, dokumen, atau topik..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-white rounded-lg focus:outline-none"
                />
              </div>
              <button type="submit" className="px-4 py-2.5 bg-cerah-teal text-white text-sm font-medium rounded-lg hover:bg-cerah-teal/90 transition-colors">
                Cari
              </button>
            </form>
          </div>
          <div className="hidden xl:block self-stretch">
            <HeroIllustration />
          </div>
        </div>

        {/* Kanal Video */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-800 dark:text-white">Kanal Video CERAH</h2>
              <p className="text-xs text-gray-400">Video penjelasan dari Direktur dan Management CERAH</p>
            </div>
            <Link to="/app/kanal-video" className="text-xs text-cerah-teal hover:underline flex items-center gap-1">
              Lihat Semua <ChevronRight size={14} />
            </Link>
          </div>

          {videosLoading ? (
            <div className="flex gap-4">
              {[...Array(5)].map((_, i) => <div key={i} className="w-52 shrink-0 h-48 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />)}
            </div>
          ) : videos.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">Belum ada video.</p>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {videos.map(v => (
                <div key={v.id} className="w-52 shrink-0 rounded-xl overflow-hidden bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm">
                  <div className="relative aspect-video bg-gray-100 dark:bg-gray-700">
                    {v.thumbnail_url
                      ? <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><Video size={28} className="text-gray-300" /></div>
                    }
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow">
                        <Play size={16} className="text-cerah-teal fill-cerah-teal ml-0.5" />
                      </div>
                    </div>
                    {v.duration && (
                      <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">{v.duration}</span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-semibold text-cerah-teal truncate">{v.speaker_name}</p>
                    <p className="text-xs text-gray-500 truncate">{v.speaker_title}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2 leading-relaxed">{v.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Aktivitas Terbaru */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-800 dark:text-white">Aktivitas Terbaru</h2>
            <Link to="/app/search" className="text-xs text-cerah-teal hover:underline flex items-center gap-1">
              Lihat semua aktivitas <ChevronRight size={14} />
            </Link>
          </div>
          {activityLoading ? (
            <div className="grid grid-cols-2 gap-3">{[...Array(6)].map((_, i) => <div key={i} className="h-14 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />)}</div>
          ) : activity.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">Belum ada aktivitas.</p>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
              {activity.map(item => (
                <Link key={item.id} to={`/app/category/${item.item_category}`}
                  className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-cerah-teal/30 transition-colors">
                  <div className="w-7 h-7 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-center shrink-0">
                    {ACTIVITY_ICONS[item.content_type] ?? <FileText size={14} className="text-gray-400" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 dark:text-white line-clamp-1">{item.item_title}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{timeAgo(item.created_at)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ── Right panel ── */}
      <div className="w-80 shrink-0 border-l border-gray-200 dark:border-gray-700 overflow-y-auto p-5 flex flex-col gap-6 bg-white dark:bg-gray-900">

        {/* Ringkasan Pengetahuan */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Ringkasan Pengetahuan</h3>
            <span className="text-xs text-gray-400">Total</span>
          </div>
          {summaryLoading ? (
            <div className="grid grid-cols-3 gap-2">{[...Array(6)].map((_, i) => <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />)}</div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {SUMMARY_ORDER.map(ct => {
                const meta = CONTENT_TYPE_META[ct]
                return (
                  <div key={ct} className={`rounded-xl p-3 flex flex-col items-center gap-1 ${meta.bg}`}>
                    {SUMMARY_ICONS[ct]}
                    <p className={`text-xl font-bold ${meta.color}`}>{getCount(ct)}</p>
                    <p className="text-[10px] text-gray-500">{meta.label}</p>
                    <p className="text-[9px] text-gray-400">{meta.unit}</p>
                  </div>
                )
              })}
            </div>
          )}
        </section>

        {/* Direktori Kontak */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Direktori Kontak</h3>
            <Link to="/app/direktori-kontak" className="text-xs text-cerah-teal hover:underline flex items-center gap-1">
              Lihat Semua <ChevronRight size={14} />
            </Link>
          </div>
          {contactsLoading ? (
            <div className="flex flex-col gap-2">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />)}</div>
          ) : contacts.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">Belum ada kontak.</p>
          ) : (
            <>
              <div className="rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800 text-gray-500 font-medium">
                      <th className="text-left px-3 py-2">Nama</th>
                      <th className="text-left px-3 py-2">Jabatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map(c => (
                      <tr key={c.id} onClick={() => navigate(`/app/direktori-kontak/${c.id}`)}
                        className="border-t border-gray-50 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                        <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200 truncate max-w-[100px]">{c.name}</td>
                        <td className="px-3 py-2 text-gray-500 truncate max-w-[100px]">{c.position ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Link to="/app/direktori-kontak"
                className="mt-3 block w-full py-2.5 text-center text-xs font-medium border border-gray-200 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                Lihat Semua Kontak
              </Link>
            </>
          )}
        </section>
      </div>
    </div>
  )
}

function HeroIllustration() {
  return (
    <svg viewBox="0 0 200 180" className="h-full max-h-44 opacity-80 pr-4" aria-hidden>
      <rect x="55" y="15" width="90" height="120" rx="10" fill="white" fillOpacity="0.08" />
      <rect x="65" y="28" width="70" height="9" rx="3" fill="white" fillOpacity="0.25" />
      <rect x="65" y="43" width="50" height="6" rx="2" fill="white" fillOpacity="0.15" />
      <rect x="65" y="55" width="55" height="6" rx="2" fill="white" fillOpacity="0.15" />
      <rect x="65" y="67" width="40" height="6" rx="2" fill="white" fillOpacity="0.15" />
      <rect x="65" y="79" width="60" height="6" rx="2" fill="white" fillOpacity="0.15" />
      <circle cx="155" cy="50" r="28" fill="white" fillOpacity="0.06" />
      <rect x="140" y="38" width="30" height="22" rx="5" fill="white" fillOpacity="0.12" />
      <circle cx="35" cy="100" r="20" fill="white" fillOpacity="0.06" />
      <path d="M25 100 Q35 88 45 95 Q55 102 65 92" stroke="#4dd9d9" strokeOpacity="0.4" strokeWidth="2" fill="none" />
    </svg>
  )
}
